import React from "react";

import type { LedgerImportDTO, LedgerImportRowDTO, LedgerPushDTO } from "../../data/ledgerImportApi";
import {
  ACTION_LABEL,
  ACTION_STYLE,
  ROW_STATUS_LABEL,
  ROW_STATUS_STYLE,
  hasBeenPushed,
  notAddedRows,
  pushVerb,
  warningCount,
} from "../../domain/ledgerImport";
import { Callout } from "../../components/Callout";
import { cn } from "../../components/cn";
import { LedgerImportRejected } from "./LedgerImportRejected";

/**
 * What the sheet would do, in four numbers — and once it has been pushed, what
 * it did.
 *
 * The counts before the rows because this is the question a CA actually has —
 * "did my three edits land, and did I break anything" — and a hundred and
 * twenty-eight rows is the wrong shape to answer it in.
 *
 * The tiles swap wholesale after a push rather than sitting alongside new ones.
 * "To be added: 12" next to "Added: 9" is two numbers about the same twelve
 * ledgers, and reading it wrong means believing twenty-one were involved.
 */

function Tile({ label, count, style }: { label: string; count: number; style: string }) {
  return (
    <div className={cn("flex flex-col gap-0.5 rounded-md px-3 py-2", style)}>
      <span className="text-xl font-semibold tabular-nums">{count}</span>
      <span className="text-xs font-medium">{label}</span>
    </div>
  );
}

export function LedgerImportSummary({
  summary,
  rows,
  push,
  pushing,
}: {
  summary: LedgerImportDTO;
  rows: LedgerImportRowDTO[];
  /** Only from a commit response — a plain read has no batch to report. */
  push?: LedgerPushDTO | null;
  pushing?: boolean;
}) {
  const warnings = warningCount(rows);
  const toPush = summary.new_rows + summary.changed_rows;

  if (hasBeenPushed(summary)) {
    return (
      <PushOutcome
        summary={summary}
        rows={rows}
        push={push ?? null}
        pushing={pushing === true}
        toPush={toPush}
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Tile label={ACTION_LABEL.new} count={summary.new_rows} style={ACTION_STYLE.new} />
        <Tile label={ACTION_LABEL.update} count={summary.changed_rows} style={ACTION_STYLE.update} />
        <Tile label={ACTION_LABEL.invalid} count={summary.invalid_rows} style={ACTION_STYLE.invalid} />
        <Tile label={ACTION_LABEL.unchanged} count={summary.unchanged_rows} style={ACTION_STYLE.unchanged} />
      </div>

      {/*
        The one number that matters for cost: unchanged rows are never sent, so
        a 128-row sheet with three edits is three Zoho calls, not 128.
      */}
      <p className="text-sm text-muted-foreground">
        {toPush === 0
          ? "Nothing in this sheet would change anything in Zoho."
          : `${toPush} ${toPush === 1 ? "ledger" : "ledgers"} would be sent to Zoho. The other ${
              summary.total_rows - toPush
            } cost nothing.`}
      </p>

      {summary.invalid_rows > 0 && (
        <Callout variant="warning">
          {summary.invalid_rows} {summary.invalid_rows === 1 ? "row needs" : "rows need"} fixing before
          {summary.invalid_rows === 1 ? " it goes" : " they go"} anywhere. Everything else is unaffected - fix
          those lines in the sheet and upload it again.
        </Callout>
      )}

      {warnings > 0 && (
        <Callout variant="warning">
          {warnings} {warnings === 1 ? "row is" : "rows are"} flagged but not blocked. Read the note on each
          before confirming.
        </Callout>
      )}

      {summary.already_pushed_at !== null && (
        <Callout variant="warning">
          This exact file was already pushed on {summary.already_pushed_at}. Zoho has no way to recognise a
          repeat, so pushing it again would add every new ledger on it a second time.
        </Callout>
      )}
    </div>
  );
}

/**
 * What the push actually did.
 *
 * Progress and result are the same component because they are the same numbers.
 * A separate progress bar that vanishes on completion means the CA watching it
 * finish sees the answer replaced at the exact moment it becomes worth reading.
 */
function PushOutcome({
  summary,
  rows,
  push,
  pushing,
  toPush,
}: {
  summary: LedgerImportDTO;
  rows: LedgerImportRowDTO[];
  push: LedgerPushDTO | null;
  pushing: boolean;
  toPush: number;
}) {
  // Refused by Zoho or held back for a missing parent: either way not in the
  // books. Counted from the rows because the summary has no skipped count.
  const notAdded = notAddedRows(rows);
  const succeeded = summary.created_rows + summary.updated_rows;
  const settled = succeeded + notAdded.length;
  const done = summary.status === "done";
  const verb = pushVerb(summary);
  const ledgers = toPush === 1 ? "ledger" : "ledgers";

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-3 gap-2">
        <Tile
          label={ROW_STATUS_LABEL.created}
          count={summary.created_rows}
          style={ROW_STATUS_STYLE.created}
        />
        <Tile
          label={ROW_STATUS_LABEL.updated}
          count={summary.updated_rows}
          style={ROW_STATUS_STYLE.updated}
        />
        <Tile label={ROW_STATUS_LABEL.failed} count={notAdded.length} style={ROW_STATUS_STYLE.failed} />
      </div>

      {/*
        A count rather than a bar. The total is known and small, and "31 of 38"
        answers "how much longer" as well as a bar does while also surviving the
        push being paused, which a bar animating to nowhere does not.
      */}
      {/*
        A clean finish gets the green callout — that is the "it worked" moment.
        A finish with rows not added keeps the plain line instead: the block
        below is the headline there, and green next to it would argue with it.
        The line counts only what landed — "155 of 155 sent" over seven
        refusals is what ZOUR-257 was about.
      */}
      {done && notAdded.length === 0 ? (
        <Callout variant="success">
          {toPush === 1 ? `1 ledger ${verb.done}.` : `All ${toPush} ledgers ${verb.done}.`}
        </Callout>
      ) : (
        <p className="text-sm text-muted-foreground">
          {pushing
            ? `Sending… ${settled} of ${toPush} done.`
            : done
              ? `${succeeded} of ${toPush} ${ledgers} ${verb.done}. ${notAdded.length} ${verb.notDone(
                  notAdded.length
                )} - see below.`
              : `Stopped at ${settled} of ${toPush}. Nothing further has been sent.`}
        </p>
      )}

      {push?.halted_reason != null && <Callout variant="warning">{push.halted_reason}</Callout>}

      {/* Two different pieces of news, and the old copy told the first one in
          both cases — "already sent are in Zoho" over a push that had sent
          nothing at all. */}
      {!pushing && !done && push?.halted !== true && (
        <Callout variant="info">
          {summary.created_rows + summary.updated_rows === 0
            ? "This push did not finish, and no ledgers have reached Zoho yet. Continuing carries on from where it stopped."
            : "This push did not finish. The ledgers already sent are in Zoho - continuing picks up from where it stopped rather than starting again."}
        </Callout>
      )}

      <LedgerImportRejected rows={notAdded} notDone={verb.notDone} />
    </div>
  );
}
